/**
 * Move contract error mappings
 * Maps error codes to user-friendly messages
 */
// Error mappings by module
export const MOVE_ERROR_MAP = {
    // Pyth price oracle
    pyth: {
        1: 'Invalid price data provided.',
        2: 'Price feed not found.',
        3: 'Price data is not fresh. Please try again in a moment.',
        4: 'Price confidence interval too wide.',
        5: 'Price data is stale.',
    },
    // Alpha lending protocol
    alpha_lending: {
        0: 'Withdrawal would leave your position undercollateralized, which is not allowed.',
        1: 'The borrow amount that you requested exceeds safe borrow limits.',
        2: 'Cannot borrow from isolated market with other borrows active.',
        3: 'You are not authorized to perform this action.',
        4: 'Position is not eligible for liquidation.',
        7: 'Invalid contract version. Please refresh and try again.',
        29: "Cannot supply into a market where you've borrowed.",
        30: "Cannot borrow from a market where you've supplied.",
        31: 'Enter a valid amount.',
        32: 'Enter a valid amount.',
        33: 'Enter a valid amount.',
        37: 'Market supply limit reached.',
    },
    // Market module
    market: {
        30: 'Market borrow limit reached.',
    },
    // Oracle module
    oracle: {
        0: 'Outdated Oracle Price',
    },
    // Generic fallback
    generic: {
        0: 'Transaction failed due to an unknown error.',
        1: 'Insufficient funds for transaction.',
        2: 'Invalid parameters provided.',
        3: 'Operation not permitted.',
    },
    // Position module
    position: {
        0: 'Position is not eligible for liquidation.',
        1: 'Incorrect asset used for liquidation.',
        2: "Error when accessing a position that hasn't been refreshed.",
        3: 'Withdrawal amount exceeds your supply balance.',
    },
};
// Module name aliases (add as needed)
const MODULE_ALIASES = {
    lending: 'alpha_lending',
    alphalend: 'alpha_lending',
    price_oracle: 'oracle',
    // Add lowercase versions for case-insensitive matching
    alpha_lending: 'alpha_lending',
    pyth: 'pyth',
    market: 'market',
    oracle: 'oracle',
    position: 'position',
};
const NON_MOVE_ERROR_MAP = {
    InsufficientCoinBalance: 'Insufficient balance for this transaction.',
    'is lower than the needed amount': 'Insufficient balance for transaction fees.',
    'User rejected the request': 'Transaction was cancelled by user.',
    'Transaction was not executed': 'Transaction was not executed. Please try again.',
    rejected: 'Transaction was cancelled by user.',
    cancelled: 'Transaction was cancelled by user.',
};
/**
 * Parse Move abort error to extract module and error code
 */
export function parseMoveAbortError(errorMessage) {
    // Handle null/undefined/non-string inputs
    if (!errorMessage || typeof errorMessage !== 'string') {
        return { module: null, errorCode: null, isValid: false };
    }
    try {
        const moduleMatch = errorMessage.match(/name: Identifier\("([^"]+)"\)/);
        const errorCodeMatch = errorMessage.match(/\}, (\d+)\)/);
        const rawModule = moduleMatch ? moduleMatch[1] : null;
        const module = rawModule ? MODULE_ALIASES[rawModule.toLowerCase()] || rawModule : null;
        const errorCode = errorCodeMatch ? parseInt(errorCodeMatch[1], 10) : null;
        // Validate error code is a valid number
        const isValidErrorCode = errorCode !== null && !isNaN(errorCode) && errorCode >= 0;
        return {
            module,
            errorCode: isValidErrorCode ? errorCode : null,
            isValid: module !== null && isValidErrorCode,
        };
    }
    catch (_a) {
        return { module: null, errorCode: null, isValid: false };
    }
}
/**
 * Get user-friendly error message
 */
export function getMoveErrorMessage(errorMessage) {
    // Handle invalid inputs early
    if (!errorMessage || typeof errorMessage !== 'string') {
        return 'Transaction failed due to an unexpected error. Please try again.';
    }
    // Handle common non-Move errors first
    for (const [errorPattern, userMessage] of Object.entries(NON_MOVE_ERROR_MAP)) {
        if (errorMessage.includes(errorPattern)) {
            return userMessage;
        }
    }
    // Check if it's a Move abort error
    if (!isMoveAbortError(errorMessage)) {
        // For non-Move errors, return a generic message or try to extract meaningful info
        return 'Transaction failed due to an unexpected error. Please try again.';
    }
    const { module, errorCode, isValid } = parseMoveAbortError(errorMessage);
    if (!isValid || errorCode === null) {
        return 'Transaction failed due to an unexpected error. Please try again.';
    }
    // Check specific module first (case-insensitive)
    if (module) {
        const normalizedModule = module.toLowerCase();
        const moduleMap = MOVE_ERROR_MAP[module] || MOVE_ERROR_MAP[normalizedModule];
        if (moduleMap && moduleMap[errorCode]) {
            return moduleMap[errorCode];
        }
    }
    // Check all modules (except generic) for the error code
    for (const [mod, moduleMap] of Object.entries(MOVE_ERROR_MAP)) {
        if (mod === 'generic')
            continue;
        if (moduleMap[errorCode]) {
            return moduleMap[errorCode];
        }
    }
    // Fallback to generic
    if (MOVE_ERROR_MAP.generic && MOVE_ERROR_MAP.generic[errorCode]) {
        return MOVE_ERROR_MAP.generic[errorCode];
    }
    return 'Transaction failed due to an unexpected error. Please try again.';
}
/**
 * Check if error is a Move abort error
 */
export function isMoveAbortError(errorMessage) {
    if (!errorMessage || typeof errorMessage !== 'string' || errorMessage.trim() === '') {
        return false;
    }
    return errorMessage.includes('MoveAbort') && errorMessage.includes('ModuleId');
}
